/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import {preventDefault, ResponseData, showDialog, validateEmail} from '@dvrd/dvr-controls';

import {CredentialResponse} from '@react-oauth/google';

import React, {useContext, useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {AppContext} from '../../misc/appContext';

import User from '../../../models/userModel';

import {ActivateHandle, LoginErrors} from '../../../util/interfaces';

import {googleLogin, login} from '../../../util/requestSender';

import {getFingerprint} from '../../../util/utils';

import {showValidationErrors} from '../../../util/componentUtil';

import LoginForm from '../../auth/login/loginForm';

import ValidateTwoFactorController from '../../twoFactor/validate/validateTwoFactorController';

import IpVerificationController from '../../auth/ipVerification/ipVerificationController';

const emptyErrors: LoginErrors = {email: null, password: null, general: null};

interface Props {
    onSignedIn: VoidFunction;
}

// The connect flow's embedded sign-in: the shared credentials form plus the
// 2FA and device-verification overlays, ending in the app context — never in
// a navigation. The surrounding stepper decides what happens next.
export default function ConnectSignInController(props: Props) {
    const {onSignedIn} = props;
    const context = useContext(AppContext);
    const {t} = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<LoginErrors>(emptyErrors);
    const [loading, setLoading] = useState(false);
    const mfaRef = useRef<ActivateHandle>(null);
    const ipVerificationRef = useRef<ActivateHandle>(null);

    function onChange(key: 'email' | 'password') {
        return function (value: string) {
            if (key === 'email') setEmail(value);
            else setPassword(value);
            setErrors({...errors, [key]: null});
        };
    }

    function onFocusField(key: keyof LoginErrors) {
        return function () {
            setErrors({...errors, [key]: null});
        };
    }

    function validateValues(): boolean {
        let valid = true;
        const _errors = {...errors};
        if (!email.length) {
            _errors.email = t('auth.login.emailRequired', 'Vul je e-mailadres in');
            valid = false;
        } else if (!validateEmail(email)) {
            _errors.email = t('auth.login.emailInvalid', 'Vul een geldig e-mailadres in');
            valid = false;
        } else if (!password.length) {
            _errors.password = t('auth.login.passwordRequired', 'Vul je wachtwoord in');
            valid = false;
        }
        if (!valid) setErrors(_errors);
        return valid;
    }

    function onSubmit() {
        if (!validateValues()) return;
        setLoading(true);
        getFingerprint().then((fingerprint: string) => {
            login({
                data: {email, password, fp: fingerprint},
                callback: requestCallback,
                errorCallback: onConnectionError
            });
        });
    }

    function onGoogleLogin(response: CredentialResponse) {
        const token = response.credential;
        if (!token) return onGoogleError();
        setLoading(true);
        getFingerprint().then((fingerprint: string) => {
            googleLogin({
                data: {token, fp: fingerprint},
                callback: requestCallback,
                errorCallback: onConnectionError
            });
        });
    }

    function onGoogleError() {
        showDialog(
            t('auth.login.googleErrorMessage', 'Er is iets missgegaan bij het verbinden met Google.'),
            t('auth.login.loginFailedTitle', 'Inloggen mislukt')
        );
    }

    function onConnectionError() {
        setLoading(false);
        setErrors(prev => ({
            ...prev,
            general: t('auth.login.connectionError', 'Verbinding mislukt — controleer je internet en probeer opnieuw.'),
            generalIsCredentials: false
        }));
    }

    function onTwoFactorSuccess(user: Record<string, any>) {
        requestCallback({user, success: true, status: 'success'});
    }

    function requestCallback(data: ResponseData) {
        setLoading(false);
        if (!data.success) {
            if (data.type === 'blocked' && data.message)
                return showDialog(data.message, t('auth.login.accountBlockedTitle', 'Account geblokkeerd'));
            else if (data.type === 'validation_error' && data.errors) return showValidationErrors(data.errors);
            else if (data.type === 'google_account')
                return showDialog(
                    t(
                        'auth.login.googleAccountMessage',
                        'Dit e-mailadres is geregistreerd met Google. Je kunt inloggen via de Google inloggen knop.'
                    ),
                    t('auth.login.googleAccountTitle', 'Google account')
                );
            else
                return setErrors({
                    ...errors,
                    general: data.message ?? t('auth.login.genericError', 'Er is iets misgegaan'),
                    generalIsCredentials: true
                });
        }
        if (data.two_factor_id) return mfaRef.current?.activate(data.two_factor_id);
        if (data.verification_id) return ipVerificationRef.current?.activate(data.verification_id, email);
        const user = data.user instanceof User ? data.user : new User(data.user);
        const firstAccount = user.accounts.length ? user.accounts[0] : null;
        context.onChangeContext({user, accounts: user.accounts, account: firstAccount}, onSignedIn);
    }

    return (
        <>
            <LoginForm
                onSubmit={onSubmit}
                onChange={onChange}
                onFocusField={onFocusField}
                onGoogleLogin={onGoogleLogin}
                onGoogleError={onGoogleError}
                onClickGoogle={preventDefault}
                email={email}
                password={password}
                loading={loading}
                errors={errors}
            />
            <ValidateTwoFactorController onSuccess={onTwoFactorSuccess} ref={mfaRef} />
            <IpVerificationController onSuccess={onTwoFactorSuccess} ref={ipVerificationRef} />
        </>
    );
}
